import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";

import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { dineroCadFromFloatingPointForeignAmount, validateCurrencyCode } from "@/utils/dinero-util";
import { SosCustomFields } from "@/utils/sos/custom-fields";
import { throwExpression } from "@/utils/util";
import { CostTaxBreakdown } from "./cost-tax-breakdown";
import { CADCurrencyConverter } from "./currency";
import { calculateBaseQuantity, itemUOMSelect, ItemUOMSelectResult } from "./item-uoms";
import { itemSelect, ItemSelectResult } from "./items";
import {
  CostCategory,
  createLineSummaryForJob,
  getItemCostCategory,
  ProcessedPurchasingLine,
  PurchaseCostsSummary,
} from "./purchasing-line-item";
import { PurchaseTaxCode, vendorPricingInformationSelect, VendorWithPricingInformation } from "./vendor";

export interface ItemReceipt extends Omit<ItemReceiptSelectResult, "lines" | "receivingCosts"> {
  vendorReceiptNum: string | null;
  linkedPos: LinkedPoDetails[];
  allLines: ItemReceiptLineItemSelectResult[];
  jobLines: ItemReceiptLineItem[];
  jobLinesSummary: PurchaseCostsSummary;

  /**
   * The percentage of the receiving applied to the current job. The receiving costs are divided up equally between all
   * jobs on the item receipt. For example, if there are three jobs on the item receipt, they are each allocated 1/3 of
   * the receiving costs.
   */
  receivingCostsJobPercent: number;

  /**
   * The 'other costs' from the item receipt with the cost amounts converted to CAD and multiplied by the otherCostsJobPercent.
   */
  jobReceivingCosts: ReceivingCost[];
}

export interface ItemReceiptLineItem extends ProcessedPurchasingLine<ItemReceiptLineItemSelectResult> {
  /** Quantity for the line item converted to the base UOM */
  baseQuantity: number;
  linkedPo: LinkedPoDetails | undefined;
}

export interface ReceivingCost extends Omit<ReceivingCostSelectResult, "amount"> {
  costCategory: CostCategory;

  /**
   * The portion of this receiving cost allocated to the current job and converted to CAD. The tax rate used for this
   * comes from the selected vendor for the cost, falling back to the item's tax rate if the vendor does not have one.
   */
  jobAmountCad: CostTaxBreakdown;
}

// NOTE: This type is specified manually rather than automatically using `SupabaseSelectResult` because the select
// query is complex enough that the performance of the dynamic type crashes the TypeScript server.
interface ItemReceiptSelectResult extends Tables<"ItemReceipt"> {
  vendor: VendorWithPricingInformation;
  currency: Pick<Tables<"Currency">, "code">;
  customFieldValues: Tables<"ItemReceiptCustomFieldValue">[];
  lines: ItemReceiptLineItemSelectResult[];
  receivingCosts: ReceivingCostSelectResult[];
}

export interface ItemReceiptLineItemSelectResult extends Pick<
  Tables<"ItemReceiptLineItem">,
  "id" | "lineNumber" | "description" | "unitprice" | "amount"
> {
  item: ItemSelectResult;
  /** Quantity for the line item in the selected ItemUOM */
  itemUOM: ItemUOMSelectResult | null;
  uomQuantity: number;
  taxCode: PurchaseTaxCode | null;
  bin: Pick<Tables<"Bin">, "id" | "number"> | null;
  job: Pick<Tables<"Job">, "id" | "name"> | null;
  linkedPurchaseOrderLineItem: LinkedPurchaseOrderLineItem | null;
}

interface LinkedPurchaseOrderLineItem {
  purchaseOrder: Pick<Tables<"PurchaseOrder">, "id" | "number">;
}

interface ReceivingCostSelectResult extends Tables<"ReceivingCost"> {
  item: ItemSelectResult;
  vendor: VendorWithPricingInformation | null;
}

export type LinkedPoDetails = NonNullable<
  ItemReceiptLineItemSelectResult["linkedPurchaseOrderLineItem"]
>["purchaseOrder"];

const itemReceiptSelect = `
  *,
  vendor:Vendor!inner(${vendorPricingInformationSelect}),
  currency:Currency!inner(code),
  customFieldValues:ItemReceiptCustomFieldValue(*),
  lines:ItemReceiptLineItem!inner(
    id,
    lineNumber,
    description,
    unitprice,
    amount,
    uomQuantity:quantity,
    itemUOM:ItemUOM!ItemReceiptLineItem_itemId_uomId_fkey(${itemUOMSelect}),
    item:Item!inner(${itemSelect}),
    taxCode:TaxCode!taxCodeId(id,name,purchaseTaxRate),
    bin:Bin(id,number),
    job:Job(id, name),
    linkedPurchaseOrderLineItem:PurchaseOrderLineItem(
      purchaseOrder:PurchaseOrder!inner(id, number)
    )
  ),
  receivingCosts:ReceivingCost(
    *,
    item:Item!inner(${itemSelect}),
    vendor:Vendor(${vendorPricingInformationSelect})
  )
`;

export async function getItemReceipts(
  jobId: number,
  CADCurrencyConverter: CADCurrencyConverter,
  todayExchangeRates: Map<string, number>,
) {
  // Fine all non-deleted item receipts that have a line item linked to the job
  const jobMatchingReceiptIdsResponse = await supabase
    .from("ItemReceiptLineItem")
    .select("itemReceiptId")
    .eq("jobId", jobId)
    .eq("deleted", false)
    .throwOnError();
  const jobMatchingReceiptIds = uniq(jobMatchingReceiptIdsResponse.data.map((x) => x.itemReceiptId));

  // Fetch all non-deleted item receipts matching the ids
  const queryResult = await supabase
    .from("ItemReceipt")
    .select<typeof itemReceiptSelect, ItemReceiptSelectResult>(itemReceiptSelect)
    .in("id", jobMatchingReceiptIds)
    .eq("deleted", false)
    .eq("lines.deleted", false)
    .eq("receivingCosts.deleted", false)
    .order("date", { ascending: false })
    .order("lineNumber", { referencedTable: "lines", ascending: true })
    .order("lineNumber", { referencedTable: "receivingCosts", ascending: true })
    .throwOnError();

  // Process all the receipts before returning
  return queryResult.data.map((receipt) =>
    processItemReceipt(receipt, jobId, CADCurrencyConverter, todayExchangeRates),
  );
}

function processItemReceipt(
  { lines: allLines, receivingCosts, ...receipt }: ItemReceiptSelectResult,
  jobId: number,
  cadCurrencyConverter: CADCurrencyConverter,
  todayExchangeRates: Map<string, number>,
): ItemReceipt {
  const vendorReceiptNum = SosCustomFields.getStringValue(receipt, "vendorReceiptNum");

  // Process the line items; Do some common processing first, then continue doing some additional processing specific to item receipts
  const currencyCode = validateCurrencyCode(receipt.currency.code);
  const { jobLines: preProcessedJobLines, jobLinesSummary } = createLineSummaryForJob(
    allLines,
    currencyCode,
    receipt.exchangeRate,
    jobId,
    cadCurrencyConverter,
  );
  const jobLines = preProcessedJobLines.map((x) => processItemReceiptLineItem(x));

  // Get a list of all linked POs from the processed line items
  const linkedPos = uniqBy(
    jobLines.map((line) => line.linkedPo).filter((x) => !!x),
    (x) => x.id,
  );

  // Process the receiving costs
  const numJobs = uniq(allLines.filter((x) => !!x.job?.id).map((x) => x.job!.id)).length;
  const receivingCostsJobPercent = 1 / numJobs;
  const jobReceivingCosts = receivingCosts.map((x) => processReceivingCost(x, receipt, numJobs, todayExchangeRates));

  return {
    ...receipt,
    vendorReceiptNum,
    allLines,
    jobLines,
    jobLinesSummary,
    linkedPos,
    receivingCostsJobPercent,
    jobReceivingCosts,
  };
}

function processItemReceiptLineItem(
  line: ProcessedPurchasingLine<ItemReceiptLineItemSelectResult>,
): ItemReceiptLineItem {
  return {
    ...line,
    baseQuantity: calculateBaseQuantity(line.uomQuantity, line.itemUOM),
    linkedPo: line.linkedPurchaseOrderLineItem?.purchaseOrder,
  };
}

function processReceivingCost(
  cost: ReceivingCostSelectResult,
  receipt: Omit<ItemReceiptSelectResult, "lines" | "receivingCosts">,
  numJobs: number,
  todayExchangeRates: Map<string, number>,
): ReceivingCost {
  const costCategory = getItemCostCategory(cost.item);

  // Calculate the receiving cost in CAD with tax
  const amountCad = getReceivingCostAmount(cost, receipt, todayExchangeRates);

  // Calculate the portion of the cost allocated to the current job
  const jobAmountCad = amountCad.allocate([1, numJobs - 1])[0]!;

  return { ...cost, costCategory, jobAmountCad };
}

/**
 * Calculate the receiving cost in CAD with tax. This assumes that the cost entered into SOS is after-tax amount in the
 * vendor's local currency.
 */
function getReceivingCostAmount(
  cost: ReceivingCostSelectResult,
  receipt: Omit<ItemReceiptSelectResult, "lines" | "receivingCosts">,
  todayExchangeRates: Map<string, number>,
): CostTaxBreakdown {
  // Return an error amount if no vendor is specified on the cost
  if (!cost.vendor) return CostTaxBreakdown.withError("No vendor specified");

  // Return an error if the vendor has no currency
  if (!cost.vendor.currency) return CostTaxBreakdown.withError(`Vendor '${cost.vendor.name}' has no currency`);

  // Decide on the exchange rate to use. If the cost vendor's currency matches the item receipt vendor's currency, use
  // the exchange rate saved on the item receipt. Otherwise, use the exchange rate for the vendor on the current date.
  // TODO: We should fall back to the vendor's exchange rate on the day the item receipt was created, not the current date.
  const costCurrencyCode = validateCurrencyCode(cost.vendor.currency.code);
  const receiptCurrencyCode = receipt.vendor.currency?.code;
  const exchangeRate =
    costCurrencyCode === receiptCurrencyCode ?
      receipt.exchangeRate
    : (todayExchangeRates.get(costCurrencyCode) ??
      throwExpression(`No exchange rate found for '${cost.vendor.name}' vendor currency '${costCurrencyCode}'`));

  // Create a CAD dinero object from the cost amount, converting from the vendor's currency
  const costAmountCad = dineroCadFromFloatingPointForeignAmount(cost.amount, costCurrencyCode, exchangeRate);

  // Create the full tax breakdown. The tax code from the item is used, falling back to the vendor's tax code if necessary
  // TODO: We may need to revisit this, it's not clear whether the amount entered on item receipts includes tax or not. It may not actually be consistent at all
  return CostTaxBreakdown.forItemAndVendor({ item: cost.item, vendor: cost.vendor, total: costAmountCad });
}
